import { useState } from "react";
import { fetchUserProfile } from "../../Api";
import PlayerSearchError from "../../components/PlayerSearchError";
import PlayerSearchForm from "../../components/PlayerSearchForm";
import Spinner from "../../components/Spinner";
import type { ProfileData } from "../../types";
import styles from "./searchPlayer.module.css";

type Props = {
  onPlayerChange: (profile: ProfileData | null) => void;
  highlight: boolean;
  onHighlightInput: () => void;
  onStepChange: (step: number) => void;
  isAutoFetching?: boolean;
  prefillPlayerId?: string;
};

export default function SearchPlayer({
  onPlayerChange,
  highlight,
  onHighlightInput,
  onStepChange,
  isAutoFetching = false,
  prefillPlayerId,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");

  const findPlayer = async (term: string) => {
    setMessage("");
    onPlayerChange(null);

    if (/\D/.test(term)) {
      setMessage("ID игрока — это только цифры, без букв и символов");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchUserProfile(Number(term));
      if (!response.success || !response.data) {
        setMessage("Игрок не найден. Проверьте ID в профиле игры");
        return;
      }
      onPlayerChange(response.data);
      onStepChange(1);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>Кому пополнить фишки?</div>
      <div className={styles.content}>
        <div className={styles.item}>
          <PlayerSearchForm
            onSubmit={findPlayer}
            disabled={isLoading || isAutoFetching}
            highlight={!isLoading && !isAutoFetching && highlight}
            initialValue={prefillPlayerId}
            onHighlightInput={() => {
              setMessage("");
              onHighlightInput();
            }}
          />
        </div>

        <div className={styles.item}>
          {highlight ? (
            <div className={styles.hint}>Укажите ID игрока, чтобы выбрать подарок</div>
          ) : isLoading || isAutoFetching ? (
            <div className={styles.spinner_container}>
              <Spinner />
            </div>
          ) : message ? (
            <PlayerSearchError message={message} />
          ) : (
            <div className={styles.hint_muted}>Введите числовой ID из профиля игрока</div>
          )}
        </div>

        <div className={styles.explanation}>
          <div className={styles.left}>
            <img src="find-id.png" className={styles.img} alt="Как найти ID игрока" />
          </div>

          <div className={`${styles.right} ${styles.card}`}>
            <h3 className={styles.title_center}>Где найти ID игрока?</h3>
            <ul className={styles.list}>
              <li className={styles.li}>Убедитесь, что установлена последняя версия приложения.</li>
              <li className={styles.li}>В лобби нажмите на иконку профиля в верхнем левом углу.</li>
              <li className={styles.li}>
                Нажмите «Копировать» рядом с игровым ID.
              </li>
              <li className={styles.li}>Вставьте ID в поле выше и нажмите «Найти».</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
